import { useState } from "react";
import { Shuffle } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { ACCENT_PALETTE } from "~/lib/accentPalette";
import { ColorSwatchGrid } from "~/pages/manifold-nextgen/components/ColorSwatchGrid";
import { FormDialog } from "~/pages/manifold-nextgen/components/FormDialog";
import {
  AGENT_BRAND_COLORS,
  Agent,
  agentPath,
  mutateAgentList,
  updateAgent,
} from "~/services/agents";
import {
  AgentAvatar,
  agentAvatarSeed,
  allShapeSeeds,
  resolveAgentAvatarColor,
  resolveShapeVariant,
} from "./AgentAvatar";

interface AgentAppearanceDialogProps {
  agent: Agent;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const initialAvatar = (agent: Agent) => {
  const seed = agentAvatarSeed(agent);
  return { seed, color: resolveAgentAvatarColor(seed, agent.avatar_color) };
};

export const AgentAppearanceDialog = ({
  agent,
  open,
  onOpenChange,
}: AgentAppearanceDialogProps) => {
  const { showSuccess, showError } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  // Captured once per mount (the dialog remounts each time it opens), so it stays
  // stable as the Reset-equivalent baseline for the isDirty check below.
  const [committed] = useState(() => initialAvatar(agent));
  const [seed, setSeed] = useState(committed.seed);
  const [color, setColor] = useState(committed.color);

  const isDirty = seed !== committed.seed || color !== committed.color;
  const shapeSeeds = allShapeSeeds();
  const currentShapeVariant = resolveShapeVariant(seed);

  const handleRandomize = () => {
    const newSeed = shapeSeeds[Math.floor(Math.random() * shapeSeeds.length)];
    setColor(AGENT_BRAND_COLORS[Math.floor(Math.random() * AGENT_BRAND_COLORS.length)]);
    setSeed(newSeed);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateAgent(agent.id, { avatar_seed: seed, avatar_color: color });
      await mutate(agentPath(agent.id));
      await mutateAgentList();
      showSuccess("Avatar saved");
      onOpenChange(false);
    } catch {
      showError("Failed to save avatar");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edit appearance"
      contentClassName="sm:max-w-2xl"
      onPrimary={handleSave}
      primaryLabel="Save"
      primaryPendingLabel="Saving…"
      primaryDisabled={!isDirty}
      isPending={isSaving}
    >
      <div className="grid grid-cols-[280px_1fr] gap-8 py-4">
        <div className="flex flex-col items-center gap-3">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Preview
          </span>
          <AgentAvatar agent={{ avatar_seed: seed, avatar_color: color }} size={96} />
          <Button variant="outline" size="sm" onClick={handleRandomize} className="w-full">
            <Shuffle className="size-4" />
            Randomize
          </Button>
        </div>

        <div className="flex flex-col gap-5">
          <div>
            <p className="mb-2 text-sm font-medium text-foreground">Color</p>
            <ColorSwatchGrid colors={ACCENT_PALETTE} color={color} onChange={setColor} />
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-foreground">Shape</p>
            <div className="flex flex-wrap gap-2.5">
              {shapeSeeds.map((sampleSeed, index) => {
                const isSelected = currentShapeVariant === resolveShapeVariant(sampleSeed);
                return (
                  <button
                    key={sampleSeed}
                    type="button"
                    aria-label={`Shape option ${index + 1}`}
                    aria-pressed={isSelected}
                    onClick={() => setSeed(sampleSeed)}
                    className="size-7 shrink-0 cursor-pointer rounded-full p-0"
                    style={{
                      boxShadow: isSelected
                        ? `0 0 0 2px var(--background), 0 0 0 4px ${color}`
                        : "0 0 0 1px var(--border)",
                    }}
                  >
                    <AgentAvatar
                      agent={{ avatar_seed: sampleSeed, avatar_color: color }}
                      size={28}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </FormDialog>
  );
};
